import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type FullCalendar from "@fullcalendar/react";
import { api, unwrapArray } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import type { CalendarConnection, CalendarEventItem } from "@/types/task";
import { CalendarView } from "@/components/calendar/CalendarView";
import { AgendaList } from "@/components/calendar/AgendaList";
import { MiniMonth } from "@/components/calendar/MiniMonth";
import { MeetingTaskDialog } from "@/components/calendar/MeetingTaskDialog";
import { TaskDetailSheet } from "@/components/calendar/TaskDetailSheet";
import { CalendarConnectionsModal } from "@/components/calendar/CalendarConnectionsModal";
import { PageHeader } from "@/components/patterns";
import { ShellPanel } from "@/components/shell/ShellContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { dateLocale, withClock } from "@/lib/dateTime";

type Layer = "meetings" | "tasks" | "deals" | "external";
const VIEWS = [
  { key: "timeGridDay", label: "Day" },
  { key: "timeGridWeek", label: "Week" },
  { key: "dayGridMonth", label: "Month" },
  { key: "agenda", label: "Agenda" },
];
const AGENDA_DAYS = 14;
const VIEW_KEY = "crm.calendar.view";
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const readView = () => {
  try {
    const saved = localStorage.getItem(VIEW_KEY);
    if (saved && VIEWS.some((v) => v.key === saved)) return saved;
  } catch {
    // Storage unavailable
  }
  // A week grid does not fit a phone
  return typeof window !== "undefined" && window.innerWidth < 768 ? "agenda" : "timeGridWeek";
};

/** Timed items of one person that overlap another one of theirs */
function findClashes(events: CalendarEventItem[]) {
  const timed = events
    .filter((e) => !e.all_day && e.kind !== "DEAL" && e.task_status !== "DONE")
    .map((e) => ({ id: e.id, who: e.assigned_to?.id || "me", from: new Date(e.start).getTime(), to: Math.max(new Date(e.end).getTime(), new Date(e.start).getTime() + 15 * 60000) }))
    .sort((a, b) => a.from - b.from);
  const clash = new Set<string>();
  for (let i = 0; i < timed.length; i++)
    for (let j = i + 1; j < timed.length && timed[j]!.from < timed[i]!.to; j++)
      if (timed[i]!.who === timed[j]!.who) (clash.add(timed[i]!.id), clash.add(timed[j]!.id));
  return clash;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const { success, error, toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const calendarRef = useRef<FullCalendar | null>(null);

  const scope = searchParams.get("scope") || "mine";
  const [range, setRange] = useState<{ start: Date; end: Date; title: string; view: string }>(() => {
    const s = new Date();
    s.setHours(0, 0, 0, 0);
    s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    return { start: s, end: new Date(s.getTime() + 7 * 86400000), title: "", view: "timeGridWeek" };
  });
  const [focus, setFocus] = useState(new Date());
  const [mode, setMode] = useState(readView);
  const [agendaStart, setAgendaStart] = useState(startOfToday);
  const agenda = mode === "agenda";
  const [layers, setLayers] = useState<Record<Layer, boolean>>({ meetings: true, tasks: true, deals: true, external: true });
  const [events, setEvents] = useState<CalendarEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState<CalendarConnection[]>([]);

  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [slot, setSlot] = useState<{ start: Date; end: Date; allDay: boolean } | null>(null);
  const [selected, setSelected] = useState<CalendarEventItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [people, setPeople] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const [newOwner, setNewOwner] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();
  const canSeeTeam = ["SUPER_ADMIN", "ADMIN", "SALES_HEAD", "SALES_MANAGER"].includes(roleCode) || ["team", "all"].includes(String(user?.data_scopes?.activity)) || ["team", "all"].includes(String(user?.data_scopes?.deal));

  // Return trip from connecting Google or Outlook
  useEffect(() => {
    const connected = searchParams.get("connected");
    const provider = searchParams.get("provider");
    const error = searchParams.get("error");
    if (!connected && !error) return;
    setBanner(connected === "true" && provider ? { type: "success", message: `Connected your ${provider} calendar. Events are syncing.` } : { type: "error", message: `Calendar connection failed: ${decodeURIComponent(error || "")}` });
    const next = new URLSearchParams(searchParams);
    ["connected", "provider", "error"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // The agenda moves in steps of two weeks and sets its own range
  useEffect(() => {
    if (!agenda) return;
    const end = new Date(agendaStart.getTime() + AGENDA_DAYS * 86400000);
    const last = new Date(end.getTime() - 86400000);
    const f = (d: Date, y = false) => d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short", ...(y ? { year: "numeric" } : {}) });
    setRange({ start: agendaStart, end, title: `${f(agendaStart)} – ${f(last, true)}`, view: "agenda" });
    setFocus(agendaStart);
  }, [agenda, agendaStart]);

  const loadConnections = useCallback(() => api.get("/api/Calendar/Connections").then((r) => setConnections(unwrapArray<CalendarConnection>(r))).catch(() => undefined), []);
  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ from: range.start.toISOString(), to: range.end.toISOString(), scope, include_external: "true", include_deals: "true" });
      setEvents(unwrapArray<CalendarEventItem>(await api.get(`/api/Calendar/Range?${p.toString()}`)));
    } catch (err) {
      console.warn("Failed to load calendar events:", err);
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, scope]);
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const layerOf = (e: CalendarEventItem): Layer => (e.kind === "DEAL" ? "deals" : e.kind === "EXTERNAL" ? "external" : e.task_type === "MEETING" ? "meetings" : "tasks");
  const counts = useMemo(() => {
    const c: Record<Layer, number> = { meetings: 0, tasks: 0, deals: 0, external: 0 };
    events.forEach((e) => (c[layerOf(e)] += 1));
    return c;
  }, [events]);
  const visible = useMemo(() => events.filter((e) => layers[layerOf(e)]), [events, layers]);
  const clashes = useMemo(() => findClashes(visible), [visible]);

  const api_ = () => calendarRef.current?.getApi();
  const go = (what: "prev" | "next" | "today") => {
    if (!agenda) return api_()?.[what]();
    const step = AGENDA_DAYS * 86400000;
    setAgendaStart((s) => (what === "today" ? startOfToday() : new Date(s.getTime() + (what === "next" ? step : -step))));
  };
  const changeView = (v: string) => {
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // Not critical
    }
    // The grid is mounted only outside the agenda, so leaving the agenda remounts it on the chosen view
    if (!agenda && v !== "agenda") api_()?.changeView(v);
    setMode(v);
  };
  /** Moves a task to another day in the agenda, keeping its time of day and length */
  const moveToDay = async (e: CalendarEventItem, day: Date) => {
    const from = new Date(e.start);
    const start = new Date(from);
    start.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
    const end = new Date(start.getTime() + Math.max(0, new Date(e.end).getTime() - from.getTime()));
    try {
      await reschedule(e.id, start, e.all_day ? undefined : end, { start: from, end: e.all_day ? undefined : new Date(e.end), allDay: e.all_day }, e.title);
    } catch {
      // reschedule already told the person
    }
  };
  const openReassign = () => {
    setNewOwner("");
    setReassignOpen(true);
    if (!people.length) api.get("/api/User/Assignable").then((r) => setPeople(unwrapArray(r))).catch(() => undefined);
  };
  const submitReassign = async () => {
    if (!selected || !newOwner) return;
    try {
      await api.put(`/api/Task/Update/${selected.id}`, { title: selected.title, due_at: selected.start, assigned_to_id: newOwner });
      const who = people.find((p) => p.id === newOwner);
      success("Reassigned", `“${selected.title}” now belongs to ${who ? `${who.first_name} ${who.last_name || ""}`.trim() : "them"}`);
      setReassignOpen(false);
      setSelected(null);
      fetchEvents();
    } catch (err: any) {
      error("Could not reassign", err?.message || "Try again");
    }
  };
  const pick = (d: Date) => {
    if (agenda) setAgendaStart(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    else api_()?.gotoDate(d);
  };
  const open = (e: CalendarEventItem) => (e.kind === "DEAL" ? navigate(`/deals/${e.id}`) : setSelected(e));
  type Where = { start: Date; end?: Date; allDay?: boolean };
  const patchTimes = (id: string, start: Date, end?: Date) => api.patch(`/api/Task/Reschedule/${id}`, { due_at: start.toISOString(), end_at: end ? end.toISOString() : undefined });
  const niceRange = (start: Date, end?: Date, allDay?: boolean) => {
    const day = start.toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short" });
    if (allDay) return day;
    const t = (d: Date) => d.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
    return `${day}, ${t(start)}${end ? ` – ${t(end)}` : ""}`;
  };

  /** Moves a task, says so with an Undo, and puts the event back if the server refuses */
  const reschedule = async (id: string, start: Date, end: Date | undefined, previous: Where, title = "Task") => {
    try {
      await patchTimes(id, start, end);
    } catch (err: any) {
      error("Could not move it", err?.message || "Try again");
      fetchEvents();
      throw err;
    }
    toast({
      type: "success",
      title: "Moved",
      message: `“${title}” · ${niceRange(start, end, previous.allDay && !end)}`,
      duration: 6000,
      action: {
        label: "Undo",
        onClick: () => {
          patchTimes(id, previous.start, previous.end)
            .then(() => fetchEvents())
            .catch((err: any) => error("Could not undo", err?.message || "Try again"));
        },
      },
    });
    fetchEvents();
  };

  const meetingsN = visible.filter((e) => e.kind === "TASK" && e.task_type === "MEETING").length;
  const timedN = visible.filter((e) => !e.all_day && e.kind === "TASK" && e.task_type !== "MEETING").length;
  const google = connections[0];
  const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(dateLocale(), withClock({ day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })) : "");
  const fmtRange = (e: CalendarEventItem) =>
    e.all_day ? new Date(e.start).toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short" }) : `${new Date(e.start).toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short" })}, ${new Date(e.start).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }))} – ${new Date(e.end).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }))}`;

  const layerRows: [Layer, string, string][] = [
    ["meetings", "Meetings", "bg-gold"],
    ["tasks", "Timed tasks", "bg-primary"],
    ["deals", "Deal close dates", "bg-success"],
    ["external", connections.length ? connections.map((c) => (c.provider === "GOOGLE" ? "Google" : "Outlook")).filter((v, i, a) => a.indexOf(v) === i).join(" · ") : "Google · Outlook", "bg-info"],
  ];

  const clashPartner = selected && clashes.has(selected.id) ? visible.find((e) => e.id !== selected.id && clashes.has(e.id) && new Date(e.start) < new Date(selected.end) && new Date(e.end) > new Date(selected.start)) : null;

  return (
    <div className="space-y-3">
      <ShellPanel title="Calendar" subtitle={scope === "team" ? "Your team" : "Your schedule"}>
        <div className="flex flex-col gap-4 px-1">
          <MiniMonth focus={focus} rangeStart={range.start} rangeEnd={range.end} onPick={pick} />
          <div className="flex flex-col gap-1">
            <span className="px-1 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Show</span>
            {layerRows.map(([key, label, dot]) => (
              <label key={key} className="flex cursor-pointer items-center gap-2 px-1 py-1 text-[13px]">
                <input type="checkbox" checked={layers[key]} onChange={() => setLayers((l) => ({ ...l, [key]: !l[key] }))} className="size-4 accent-primary" />
                <span className={cn("size-2 rounded-full", dot)} aria-hidden="true" />
                <span className="flex-1 truncate">{label}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{counts[key]}</span>
              </label>
            ))}
            {canSeeTeam && (
              <label className="flex cursor-pointer items-center gap-2 px-1 py-1 text-[13px]">
                <input
                  type="checkbox"
                  checked={scope === "team"}
                  onChange={() => {
                    const next = new URLSearchParams(searchParams);
                    scope === "team" ? next.delete("scope") : next.set("scope", "team");
                    setSearchParams(next);
                  }}
                  className="size-4 accent-primary"
                />
                <span className="size-2 rounded-full bg-muted-foreground" aria-hidden="true" />
                <span className="flex-1">Team calendar</span>
              </label>
            )}
          </div>
          <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-card px-3 py-2.5">
            <span className="text-[13px] font-semibold">{google ? (google.provider === "GOOGLE" ? "Google Calendar" : "Outlook Calendar") : "Calendar sync"}</span>
            {google ? (
              <span className={cn("text-[11.5px]", google.sync_status !== "CONNECTED" ? "text-danger" : "text-success")}>
                {google.sync_status !== "CONNECTED" ? "Sync problem" : google.last_synced_at ? `Synced ${when(google.last_synced_at)}` : "Waiting for first sync"} · {google.provider_account_email}
              </span>
            ) : (
              <span className="text-[11.5px] text-muted-foreground">Not connected. Meetings you make here can appear in Google or Outlook.</span>
            )}
            <button type="button" onClick={() => setConnectionsOpen(true)} className="pt-1 text-left text-xs font-medium text-primary hover:underline">
              {google ? "Manage connection" : "Connect a calendar"}
            </button>
          </div>
        </div>
      </ShellPanel>

      <PageHeader
        title="Calendar"
        actions={
          <Button
            size="sm"
            onClick={() => {
              setSlot(null);
              setCreateOpen(true);
            }}
          >
            <Plus className="mr-1.5 size-4" />
            New meeting
          </Button>
        }
      />

      {banner && (
        <div role="status" className={cn("flex items-center justify-between rounded-lg border px-3.5 py-2.5 text-xs font-medium", banner.type === "success" ? "border-success/30 bg-success-soft text-success" : "border-danger/30 bg-danger-soft text-danger")}>
          <span className="flex items-center gap-2">
            {banner.type === "success" ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
            {banner.message}
          </span>
          <button type="button" aria-label="Dismiss" onClick={() => setBanner(null)} className="px-2 text-base leading-none">
            ×
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        <Button variant="outline" size="sm" onClick={() => go("today")}>
          Today
        </Button>
        <Button variant="outline" size="icon-sm" aria-label="Previous" onClick={() => go("prev")}>
          <ChevronLeft className="size-4" />
        </Button>
        <Button variant="outline" size="icon-sm" aria-label="Next" onClick={() => go("next")}>
          <ChevronRight className="size-4" />
        </Button>
        <h2 className="text-[19px] font-semibold tracking-tight">{range.title}</h2>
        <span className="text-[13px] text-muted-foreground">
          {meetingsN} meeting{meetingsN === 1 ? "" : "s"} · {timedN} timed task{timedN === 1 ? "" : "s"}
          {clashes.size > 0 && <span className="font-semibold text-danger"> · {Math.ceil(clashes.size / 2)} clash{Math.ceil(clashes.size / 2) === 1 ? "" : "es"}</span>}
        </span>
        <div className="flex-1" />
        <div role="group" aria-label="Range" className="flex overflow-hidden rounded-lg border border-border">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              aria-pressed={mode === v.key}
              onClick={() => changeView(v.key)}
              className={cn("h-[30px] px-3 text-xs", mode === v.key ? "bg-sidebar font-semibold text-white" : "bg-card text-muted-foreground hover:text-foreground")}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {agenda ? (
        <AgendaList events={visible} clashIds={clashes} selectedId={selected?.id} showOwner={scope === "team"} loading={loading} rangeStart={range.start} rangeDays={AGENDA_DAYS} onOpen={open} onNew={() => (setSlot(null), setCreateOpen(true))} onMove={moveToDay} />
      ) : (
      <CalendarView
        calendarRef={calendarRef}
        initialView={mode}
        events={visible}
        clashIds={clashes}
        loading={loading}
        onEventClick={open}
        onSelectSlot={(start, end, allDay) => {
          setSlot({ start, end, allDay });
          setCreateOpen(true);
        }}
        onRescheduleEvent={(id, start, end, previous) => reschedule(id, start, end, previous, events.find((e) => e.id === id)?.title)}
        onDatesSet={(start, end, title, view) => {
          setRange({ start, end, title, view });
          setFocus(calendarRef.current?.getApi().getDate() || start);
        }}
      />
      )}

      {selected && (
        <section aria-label="Selected item" className="flex flex-wrap items-start gap-5 rounded-lg border border-border bg-card px-3.5 py-3">
          <div className="flex min-w-[280px] flex-1 flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gold-ink">
              {selected.kind === "EXTERNAL" ? `${selected.provider === "GOOGLE" ? "Google" : "Outlook"} event` : selected.task_type === "MEETING" ? "Meeting" : (selected.task_type || "Task").replace("_", " ").toLowerCase()} · {fmtRange(selected)}
            </span>
            <span className="text-[15px] font-semibold">{selected.title}</span>
            {(selected.location || selected.attendees?.length) && (
              <span className="text-[13px] text-muted-foreground">
                {selected.location}
                {selected.location && selected.attendees?.length ? " · " : ""}
                {selected.attendees?.length ? `with ${selected.attendees.slice(0, 3).map((a) => a.name || a.email).join(", ")}${selected.attendees.length > 3 ? ` +${selected.attendees.length - 3}` : ""}` : ""}
              </span>
            )}
            {clashPartner && <span className="text-xs font-semibold text-warning">Overlaps “{clashPartner.title}”. Reschedule one?</span>}
          </div>
          {selected.related_id && selected.related_type && (
            <div className="flex min-w-[220px] flex-1 flex-col gap-0.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Linked {selected.related_type.toLowerCase()}</span>
              <Link to={`/${{ LEAD: "leads", DEAL: "deals", ACCOUNT: "accounts", CONTACT: "contacts", QUOTE: "quotes" }[selected.related_type]}/${selected.related_id}`} className="text-[13px] font-semibold text-primary hover:underline">
                {selected.related_name || selected.related_type}
              </Link>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {selected.online_link && (
              <Button asChild size="sm" className="bg-sidebar text-white hover:bg-sidebar/90">
                <a href={selected.online_link} target="_blank" rel="noreferrer">
                  Join
                </a>
              </Button>
            )}
            {selected.location && (
              <Button asChild variant="outline" size="sm">
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.location)}`} target="_blank" rel="noreferrer">
                  Get directions
                </a>
              </Button>
            )}
            {selected.kind === "TASK" && selected.task_status !== "DONE" && canSeeTeam && (
              <Button variant="outline" size="sm" onClick={openReassign}>
                Reassign
              </Button>
            )}
            {selected.kind === "TASK" && (
              <Button variant="outline" size="sm" onClick={() => setDetailOpen(true)}>
                Open details
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
              Close
            </Button>
          </div>
        </section>
      )}

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reassign “{selected?.title}”</DialogTitle>
            <DialogDescription>It moves to their calendar and task list. The time and the linked record stay.</DialogDescription>
          </DialogHeader>
          <SearchableSelect value={newOwner} onChange={(e) => setNewOwner(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" aria-label="New owner">
            <option value="">Choose a person…</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.first_name} {p.last_name || ""}
              </option>
            ))}
          </SearchableSelect>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!newOwner} onClick={submitReassign}>
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MeetingTaskDialog open={createOpen} onOpenChange={setCreateOpen} initialStart={slot?.start} initialEnd={slot?.end} initialAllDay={slot?.allDay} onSaved={() => fetchEvents()} />
      <TaskDetailSheet event={selected} open={detailOpen} onOpenChange={setDetailOpen} onUpdated={() => (fetchEvents(), setSelected(null))} onDeleted={() => (fetchEvents(), setSelected(null))} />
      <CalendarConnectionsModal open={connectionsOpen} onOpenChange={setConnectionsOpen} onConnectionsChanged={() => (fetchEvents(), loadConnections())} />
    </div>
  );
}
